/**
 * This module handles conversion tracking via the Google Ads gtag.js SDK in the browser.
 * Conversions are only tracked if the user has given marketing consent.
 *
 * This module also manages user consent for marketing/advertising tracking.
 * Consent is only requested from users arriving via Google Ads (detected by gclid, gbraid, wbraid).
 * For all other users, no marketing tracking is performed.
 *
 * Flow:
 * 1. User arrives from Google Ads (URL has gclid/gbraid/wbraid)
 * 2. We show a consent banner (if consent hasn't been given/denied yet)
 * 3. If user accepts, we load Google Ads script and update consent signals
 * 4. If user declines, we don't load the script
 * 5. User can later change their preference via Privacy Settings
 */

import { isNil } from "lodash";
import {
  Codec,
  setStoragePersistedState,
  StringCodec,
  useStoragePersistedState,
} from "use-storage-persisted-state";
import { LOCAL_STORAGE_KEYS as lsk } from "../app/storageKeys";
import { UserAudience } from "../app/settings/generalSettingsTypes";
import { devOrEmulatorEnv } from "../utils";

export type MarketingConsentStatus = "granted" | "denied" | "pending";

const AD_PARAMS = ["gclid", "wbraid", "gbraid"] as const;

/**
 * Check if the current page load is from an ad click
 */
export function hasAdTrafficUrlParams(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return AD_PARAMS.some((param) => params.has(param));
}

/**
 * Check if the user has ever visited from an ad
 * (We remember this so we can show privacy settings option even on subsequent visits)
 */
export function hasEverVisitedFromAd(): boolean {
  if (typeof window === "undefined") return false;
  return !isNil(getMarketingConsentStatus());
}

/**
 * React hook to check if user has ever visited from an ad.
 * Useful for conditionally rendering components.
 * This hook updates the returned value reactively.
 */
export function useHasEverVisitedFromAd(): boolean {
  const marketingConsentStatus = useMarketingConsentStatus();
  return !isNil(marketingConsentStatus);
}

/**
 * Mark the user as having visited from an ad
 */
export function touchAdVisitor(): void {
  const marketingConsentStatus = getMarketingConsentStatus();
  if (isNil(marketingConsentStatus)) {
    // If consent not set yet, initialize to pending
    setMarketingConsent("pending");
  }
}

/**
 * Get the current marketing consent status
 */
export function getMarketingConsentStatus(): MarketingConsentStatus | null {
  if (typeof window === "undefined") return null;
  const stored = localStorage.getItem(lsk.MARKETING_CONSENT);
  if (stored === "granted") return "granted";
  if (stored === "denied") return "denied";
  if (stored === "pending") return "pending";
  return null;
}

export function useMarketingConsentStatus(): MarketingConsentStatus | null {
  const [status] = useStoragePersistedState<MarketingConsentStatus | null>(
    lsk.MARKETING_CONSENT,
    null,
    {
      codec: StringCodec as Codec<MarketingConsentStatus | null>,
      pollingIntervalMs: null,
    },
  );
  return status;
}

/**
 * Set the marketing consent status
 */
export function setMarketingConsent(status: MarketingConsentStatus): void {
  setStoragePersistedState(lsk.MARKETING_CONSENT, status);
  if (status === "granted" || status === "denied") {
    gtagUpdateConsent(status);
  }
  loadGtagJsIfNotLoaded();
}

/**
 * Check if we should show the consent banner
 */
export function shouldShowConsentBanner(): boolean {
  if (typeof window === "undefined") return false;

  const consentStatus = getMarketingConsentStatus();
  const isFromAd = hasAdTrafficUrlParams();

  // Mark as ad visitor if arriving from ad
  if (isFromAd) {
    touchAdVisitor();
  }

  // Show banner if: user came from ad AND hasn't decided yet
  return isFromAd && consentStatus === "pending";
}

// Type declaration for gtag
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (
      command: string,
      targetOrAction: string | Date,
      config?: Record<string, unknown>,
    ) => void;
    __gadsLoaded?: boolean;
  }
}

/**
 * Update Google Consent Mode signals
 */
export function gtagUpdateConsent(status: "granted" | "denied"): void {
  if (typeof window === "undefined") return;

  // gtag should be initialized in gatsby-ssr.tsx
  const gtag = window.gtag;
  if (typeof gtag !== "function") {
    console.warn("gtag not found, consent update skipped");
    return;
  }

  gtag("consent", "update", {
    ad_storage: status,
    ad_user_data: status,
    ad_personalization: status,
  });
}

/**
 * Load the Google Ads gtag.js script dynamically if not already loaded
 * Does not load the script in dev or emulator environments
 */
export function loadGtagJsIfNotLoaded(): void {
  if (typeof window === "undefined") return;
  if (window.__gadsLoaded) return;

  const gtagId = process.env.GATSBY_GOOGLE_ADS_ID;
  if (!gtagId) {
    console.warn("GATSBY_GOOGLE_ADS_ID not configured");
    return;
  }

  window.__gadsLoaded = true;

  if (devOrEmulatorEnv()) {
    console.log("📊 [GTAG]: Skipping gtag.js load in dev/emulator environment");
    return;
  }

  // Load gtag.js script
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gtagId}`;
  document.head.appendChild(script);

  // Configure gtag for this property
  const gtag = window.gtag;
  if (typeof gtag === "function") {
    gtag("js", new Date());
    gtag("config", gtagId, {
      // Allow enhanced conversions
      allow_enhanced_conversions: true,
    });
  }
}

// Conversion labels from Google Ads
// These need to be configured in Google Ads and their values set in environment variables
// Format: Alphanumeric string found in the event snippet after 'AW-XXXX/'
const CONVERSION_LABELS = {
  lead_pro_early_access:
    process.env.GATSBY_GOOGLE_ADS_CONVERSION_LABEL_LEAD_PRO,
  demo_started_pro: process.env.GATSBY_GOOGLE_ADS_CONVERSION_LABEL_DEMO_PRO,
} as const;

type ConversionEventName = keyof typeof CONVERSION_LABELS;

/**
 * Track a conversion event
 */
export function gtagEventConversion(
  eventName: ConversionEventName,
  value?: number,
): void {
  if (typeof window === "undefined") return;

  const gtagId = process.env.GATSBY_GOOGLE_ADS_ID;
  if (!gtagId) return;

  const gtag = window.gtag;
  if (typeof gtag !== "function") {
    console.warn("gtag not found, conversion event skipped");
    return;
  }

  const conversionLabel = CONVERSION_LABELS[eventName];
  if (!conversionLabel) {
    console.warn(`No conversion label configured for event: ${eventName}`);
    return;
  }

  gtag("event", "conversion", {
    send_to: `${gtagId}/${conversionLabel}`,
    ...(value !== undefined && { value }),
  });
}

/**
 * Initialize marketing consent on page load
 * Should be called early in app lifecycle
 */
export function initializeMarketingConsent(): void {
  if (typeof window === "undefined") return;

  // If user already consented, load the Google Ads script
  const consentStatus = getMarketingConsentStatus();
  if (consentStatus === "granted") {
    /**
     * On fresh page loads, we need to update consent mode to ads script. We can do this before
     * loading the ads script, because gatsby-ssr.tsx initializes a simple gtag function that
     * queues commands.
     */
    gtagUpdateConsent("granted");
  }
  if (consentStatus !== null) {
    /**
     * Load the script for all ad visitors. Without consent, the script will operate
     * in limited mode (no cookies, no identifiers). If consent was granted, full tracking
     * will be enabled via the consent update above.
     * For non-ad visitors, we never load the script.
     */
    loadGtagJsIfNotLoaded();
  }
}

let demoStartedProSent = false;
export function maybeTrackDemoStartedProConversion(
  audience: UserAudience | null,
  audienceConfirmed: boolean,
): void {
  if (typeof window === "undefined") return;
  if (audience !== "pro" || !audienceConfirmed) return;
  if (demoStartedProSent) return;
  demoStartedProSent = true;
  void gtagEventConversion("demo_started_pro");
}

export function trackLeadProEarlyAccess(): void {
  void gtagEventConversion("lead_pro_early_access");
}
